import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "./axiosBaseQuery";
import { creditApi } from "./creditApi";
import type {
  AddMemberRequest,
  AddMemberResult,
  AllocationResult,
  CreateTeamRequest,
  CreatedTeamOverview,
  MemberEmailResolution,
  ReclaimResult,
  SetMemberStatusRequest,
  SetMemberStatusResult,
  TeamMemberDetail,
  TeamMembersQuery,
  TeamOverview,
  TeamPoolLogPage,
  TeamPoolLogQuery,
  TransferRequest,
} from "@/lib/types/team.types";

/**
 * Team management API.
 *
 * Talks to the Express middleware at `/api/team/*`, which resolves the acting
 * user from the session cookie and the team from that user's own membership — so
 * no user id or team id is ever sent from the browser and
 * `credentials: "include"` carries the session instead.
 *
 * Two mutations are deliberately two-step, because both absorb or move somebody
 * else's credit and the admin has to agree first:
 *
 *   addMember      — 409 CONFIRM_EXISTING_USER, repeat with `confirm: true`
 *   setMemberStatus — 409 CONFIRM_RECLAIM_REMAINING, repeat with
 *                     `reclaimRemaining: true | false`
 *
 * Both codes arrive in `error.data.details.code`; see TeamApiError.
 *
 * CACHE NOTE: this is a separate createApi slice from creditApi, and RTK Query
 * tags do not cross slice boundaries. Several team mutations nevertheless change
 * the SIGNED-IN USER'S OWN balance - creating a team sweeps their credit into the
 * pool, and an admin can allocate to or reclaim from themselves - so those
 * mutations have to invalidate creditApi explicitly. Without it the top-bar badge
 * and the usage screen keep showing the balance from before the transfer, which
 * reads as the transfer having silently not happened.
 */

interface Envelope<T> {
  success: boolean;
  message?: string;
  data: T;
}

const EMPTY_LOG_PAGE: TeamPoolLogPage = {
  items: [],
  page: 1,
  limit: 10,
  hasMore: false,
};

/**
 * Refreshes the signed-in user's own balance and ledger after a mutation that may
 * have moved credit into or out of it. Attached as `onQueryStarted` to every team
 * mutation that can touch the acting user's account.
 *
 * Waits for the mutation to settle first, and stays silent if it failed: nothing
 * moved in that case, and invalidating anyway would fire two pointless requests
 * on every rejected reclaim.
 */
async function refreshOwnCreditCache(
  _arg: unknown,
  { dispatch, queryFulfilled }: { dispatch: (action: unknown) => unknown; queryFulfilled: Promise<unknown> }
) {
  try {
    await queryFulfilled;
    dispatch(creditApi.util.invalidateTags(["CreditBalance", "CreditHistory"]));
  } catch {
    // The mutation failed, so no balance changed - nothing to refresh.
  }
}

export const teamApi = createApi({
  reducerPath: "teamApi",
  // Routed through axiosBaseQuery so team calls share the session handling in
  // lib/axios — see the note there. Matters most here: these are the endpoints a
  // disabled member hits, and the 403 has to actually sign them out.
  baseQuery: axiosBaseQuery("/api/team"),
  tagTypes: ["Team", "TeamMembers", "TeamPoolLogs"],
  // Not refetchOnFocus: getMyTeam is refetched on page mounts (keepUnusedDataFor
  // is 60s) and again inside the credit guard when the insufficient-credits
  // modal opens, so focus events would only add noise.
  refetchOnReconnect: true,
  endpoints: (builder) => ({
    /**
     * The signed-in user's team, or null when they belong to none — which is the
     * cue to render the "Create a team" button rather than the team screen.
     */
    getMyTeam: builder.query<TeamOverview | null, void>({
      query: () => "/",
      transformResponse: (response: Envelope<TeamOverview | null>) => response?.data ?? null,
      providesTags: ["Team"],
      keepUnusedDataFor: 60,
    }),

    /**
     * Creates a team with the signed-in user as its admin. Idempotent: a second
     * click returns the team they already own with `created: false`.
     */
    createMyTeam: builder.mutation<CreatedTeamOverview, CreateTeamRequest | void>({
      query: (body) => ({ url: "/", method: "POST", body: body ?? {} }),
      transformResponse: (response: Envelope<CreatedTeamOverview>) => response.data,
      invalidatesTags: ["Team", "TeamMembers"],
      onQueryStarted: refreshOwnCreditCache,
    }),

    /** The member table: names, emails, roles, statuses and their own balances. */
    getTeamMembers: builder.query<TeamMemberDetail[], TeamMembersQuery | void>({
      query: (args) => {
        const { page = 1, limit = 50, status } = args ?? {};
        return { url: "/members", params: { page, limit, ...(status ? { status } : {}) } };
      },
      transformResponse: (response: Envelope<TeamMemberDetail[]>) => response?.data ?? [],
      providesTags: ["TeamMembers"],
      keepUnusedDataFor: 30,
    }),

    /**
     * Dry run of "add this email": changes nothing and returns the code the
     * prompt should be built from, plus the person's name when an account
     * exists. Use it on blur/submit of the email field, then call addMember.
     */
    resolveMemberEmail: builder.query<MemberEmailResolution, string>({
      query: (email) => ({ url: "/members/resolve", params: { email } }),
      transformResponse: (response: Envelope<MemberEmailResolution>) => response.data,
      keepUnusedDataFor: 0,
    }),

    /**
     * Adds a teammate by email. Two rejections are prompts rather than bugs:
     * 409 `CONFIRM_EXISTING_USER` (repeat with `confirm: true`) and 404
     * `INVITE_NEW` (repeat with `firstName` to create the account and join them
     * in one call).
     */
    addTeamMember: builder.mutation<AddMemberResult, AddMemberRequest>({
      query: (body) => ({ url: "/members", method: "POST", body }),
      transformResponse: (response: Envelope<AddMemberResult>) => response.data,
      invalidatesTags: ["Team", "TeamMembers"],
    }),

    /**
     * Disables or re-activates a teammate. Disabling somebody who still holds
     * credit fails with 409 `CONFIRM_RECLAIM_REMAINING` and
     * `details.remainingBalance`; repeat with `reclaimRemaining` set either way.
     * A disabled member can no longer be allocated to.
     */
    setTeamMemberStatus: builder.mutation<SetMemberStatusResult, SetMemberStatusRequest>({
      query: (body) => ({ url: "/members/status", method: "POST", body }),
      transformResponse: (response: Envelope<SetMemberStatusResult>) => response.data,
      invalidatesTags: ["Team", "TeamMembers", "TeamPoolLogs"],
      onQueryStarted: refreshOwnCreditCache,
    }),

    /**
     * Moves credit from the pool into a member's own balance. Settles
     * synchronously; `kind: "processing"` means the pool has paid out and the
     * member leg is being finished by the service's worker.
     */
    allocateTeamCredit: builder.mutation<AllocationResult, TransferRequest>({
      query: (body) => ({ url: "/allocate", method: "POST", body }),
      transformResponse: (response: Envelope<AllocationResult>) => response.data,
      invalidatesTags: ["Team", "TeamMembers", "TeamPoolLogs"],
      onQueryStarted: refreshOwnCreditCache,
    }),

    /**
     * Takes credit back from a member into the pool. It cannot drive their balance
     * negative - the member leg goes through the service's debit queue - and it
     * normally settles inside the request, so `kind: "applied"` means the `pool`
     * in the result is already the new figure. `kind: "queued"` means a worker
     * claimed the member debit first and the pool catches up shortly.
     */
    reclaimTeamCredit: builder.mutation<ReclaimResult, TransferRequest>({
      query: (body) => ({ url: "/reclaim", method: "POST", body }),
      transformResponse: (response: Envelope<ReclaimResult>) => response.data,
      invalidatesTags: ["Team", "TeamMembers", "TeamPoolLogs"],
      onQueryStarted: refreshOwnCreditCache,
    }),

    /** The pool ledger: funding in, allocations out, reclaims back. */
    getTeamPoolLogs: builder.query<TeamPoolLogPage, TeamPoolLogQuery | void>({
      query: (args) => {
        const { page = 1, limit = 10, status, category, memberUserId } = args ?? {};
        return {
          url: "/logs",
          params: {
            page,
            limit,
            ...(status ? { status } : {}),
            ...(category ? { category } : {}),
            ...(memberUserId ? { memberUserId } : {}),
          },
        };
      },
      transformResponse: (response: Envelope<TeamPoolLogPage>) => response?.data ?? EMPTY_LOG_PAGE,
      providesTags: ["TeamPoolLogs"],
      keepUnusedDataFor: 30,
    }),
  }),
});

export const {
  useGetMyTeamQuery,
  useCreateMyTeamMutation,
  useGetTeamMembersQuery,
  useResolveMemberEmailQuery,
  useLazyResolveMemberEmailQuery,
  useAddTeamMemberMutation,
  useSetTeamMemberStatusMutation,
  useAllocateTeamCreditMutation,
  useReclaimTeamCreditMutation,
  useGetTeamPoolLogsQuery,
} = teamApi;

export default teamApi;
